/*--------------------------------------------------------------------------------------------
Program     :  TimeCalculationService.js.js
Description :  Main service  program for Map and Track calculation
Date        :  29/08/2024 
Created By  :  Fode/Jelbin
---------------------------------------------------------------------------------------------
TicketNo         Author          Date            ChangeDesc
---------------------------------------------------------------------------------------------
29082024        - Fode/Jelbin     -  29/08/2024        Created
---------------------------------------------------------------------------------------------*/
import MainService from './MainService';
import { jwtDecode } from "jwt-decode";

class TimeCalculationService {

  static googleapiKey = null;

  static async getCompanyData() {
    if (this.googleapiKey) return;
    try {
      const response = await MainService.getCompanyData();
      const decryptResponse = response.data.envinfo;
      const decodedCompanyData = jwtDecode(decryptResponse);
      this.googleapiKey = decodedCompanyData.GOOGLE_API;
    } catch (err) {
      console.error("Error fetching company Data:", err);
    }
  }

  static async calculateTravelTime(originCoords, destCoords) {
    if (!originCoords?.lat || !destCoords?.lat) return 0;
    let ForMinTime = 0;
    let TrvlTimePercnt = 0;

    try {
      // Start both promises in parallel. 
      // promises Means program wait to get the response from both service,
      // then run remaining part.
      const [TravelConstresponse, distanceMatrixResponse] = await Promise.all([
        MainService.getTravelTimeConstant(),
        MainService.getDistanceMatrix(
          [originCoords.lat, originCoords.lng],
          [destCoords.lat, destCoords.lng]
        )
      ]);
      // Process travel time constants
      const ZTravelConstresponseValue = TravelConstresponse.data.value || [];
      ZTravelConstresponseValue.forEach(item => {
        if (item.NAME === "FORMINTTIME") {
          ForMinTime = item.VALUE;
        } else if (item.NAME === "TRAVTIMEPER") {
          TrvlTimePercnt = item.VALUE;
        }
      });
      // Process Google Distance Matrix response
      if (
        distanceMatrixResponse.data?.status === 'OK' &&
        distanceMatrixResponse.data.rows[0].elements[0].status === 'OK'
      ) {
        const matrixTravelTime = distanceMatrixResponse.data.rows[0].elements[0].duration_in_traffic.value / 3600;
        const ztravelTime = await this.traveltimeCalculateWithConstant(
          matrixTravelTime,
          ForMinTime,
          TrvlTimePercnt
        );
        return ztravelTime;
      }
      //return 0;
    } catch (err) {
      console.error('Error calculating travel time:', err);
      return 0;
    }
  }


  static async traveltimeCalculateWithConstant(matrixtraveltime, ForMinTime, TrvlTimePercnt) {
    // convert the ForMinTime value to hour
    var hoursForMinTime = Math.floor(ForMinTime / 60);
    if (matrixtraveltime >= hoursForMinTime) {
      if (TrvlTimePercnt < 0) {
        // If negative, reduce travel time by the percentage amount
        let reductionPercent = Math.abs(TrvlTimePercnt);
        let reducetrvlResult = matrixtraveltime * (1 - reductionPercent / 100);
        return reducetrvlResult;
      } else {
        // If positive, increase travel time by the percentage amount
        let increaseTrvlResult = matrixtraveltime * (1 + TrvlTimePercnt / 100);
        return increaseTrvlResult;
      }
    } else {
      return matrixtraveltime;
    }
  }

  static parseTime(timeString) {
    if (timeString !== undefined) {
      const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
      return hours + minutes / 60;
    }
    else {
      return "";
    }
  }


  static formatTime(hours) {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  // Adding two times
  static addTimeStrings(time1, time2) {
    const [h1, m1] = time1.split(":").map(Number);
    const [h2, m2] = time2.split(":").map(Number);

    const totalMinutes = h1 * 60 + m1 + h2 * 60 + m2;

    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
    const minutes = String(totalMinutes % 60).padStart(2, "0");
    return `${hours}:${minutes}`;
  }


  static async calculateAgentSchedule(
    agent,
    durationField,
    fielduserstartTime,
    tskTravelTimeField,
    finalTravelTimeData,
    selectedtraveler,
    mapVisibilityDate,
    ZDATERANGE,
    selectedSourceMapname
  ) {
    if (!agent?.coordinates || !agent.details?.length) {
      return { ...agent, totalTime: 0, finalTravelTimeData: [] };
    }

    const coordinatesInfo = this.getValidCoordinates(agent, mapVisibilityDate);
    let previousCoords = agent.coordinates;
    let totalTime = 0;
    let currentTime = this.parseTime(agent[fielduserstartTime] || "09:00");

    const updatedDetails = [];
    const updatedTravelData = [...finalTravelTimeData]; // Safe clone for mutation

    for (const detail of agent.details) {
      if (detail[ZDATERANGE].startsWith(mapVisibilityDate)) {
        const updatedDetail = { ...detail };

        // Travel time to POI or directly to task
        if (detail.pointsOfInterest?.length > 0) {
          const poiTravelTime = await this.calculateTravelTime(previousCoords, detail.pointsOfInterest[0].coordinates);
          const taskTravelTime = await this.calculateTravelTime(detail.pointsOfInterest[0].coordinates, detail.coordinates);
          updatedDetail.poiTravelTime = poiTravelTime;
          updatedDetail.travelTimeTo = poiTravelTime + taskTravelTime;
          totalTime += poiTravelTime + taskTravelTime;
        } else {
          const travelTime = await this.calculateTravelTime(previousCoords, detail.coordinates);
          updatedDetail.travelTimeTo = travelTime;
          totalTime += travelTime;
        }

        // Add task duration
        if (detail[durationField]) {
          totalTime += this.parseTime(detail[durationField]);
        }

        previousCoords = detail.coordinates;
        updatedDetails.push(updatedDetail);

        // Return journey time
        let finalTravelTime = 0;
        const pois = agent.pointsOfInterest || [];
        const matchPoi = pois.find(poi => poi.POIASSIGNDATE.startsWith(mapVisibilityDate));
        if (matchPoi) {
          const poiTravelTime = await this.calculateTravelTime(previousCoords, matchPoi.coordinates);
          const homeTravelTime = await this.calculateTravelTime(matchPoi.coordinates,
            coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates);
          finalTravelTime = poiTravelTime + homeTravelTime;
        } else {
          finalTravelTime = await this.calculateTravelTime(previousCoords,
            coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates);
        }

        totalTime += finalTravelTime;

        // Format new entry
        const formattedTravelTime = this.formatTime(finalTravelTime);
        const currentDateKey = detail[ZDATERANGE].slice(0, 10);
        const travelerId = agent[selectedtraveler];

        // Try updating if match exists
        const matchIndex = updatedTravelData.findIndex(data =>
          data.TRAVELER === travelerId &&
          data.MAPNAME === selectedSourceMapname &&
          data.TRAVELDATE.slice(0, 10) === currentDateKey &&
          data.TRAVLRTYPE === "TeamLeader"
        );

        if (matchIndex !== -1) {
          updatedTravelData[matchIndex] = {
            ...updatedTravelData[matchIndex],
            TRAVELTIME: formattedTravelTime
          };
        } else {
          // Add new record
          updatedTravelData.push({
            MAPNAME: selectedSourceMapname,
            TRAVELDATE: detail[ZDATERANGE],
            TRAVELER: travelerId,
            TRAVELTIME: formattedTravelTime,
            TRANSID: 0,
            USERPOI: "",
            TRAVLRTYPE: "TeamLeader",
            TEAMLEADER: agent[selectedtraveler]
          });
        }
      } else {
        updatedDetails.push(detail);
      }
    }
    return {
      ...agent,
      details: updatedDetails,
      totalTime,
      finalTravelTime: updatedTravelData.filter(
        data => data.TRAVELER === agent[selectedtraveler] &&
          data.TRAVLRTYPE === "TeamLeader"
      )
    };
  }

  // This function create a new payload which includes the participants total task time based on date
  // The input combinedatapayload conatins the latest value of the partcipant taravel time
  // When any particpant add or delete, then the combinedatapayload will update and this function gets the modified payload as input.

  static async participantTotalTaskTime(
    selectedtraveler, ZDATERANGE, participantsData, mergeparticipantFields,
    combinedatapayload, durationField) {

    // Step1: get all traveller List
    const allSelectedTravelers = combinedatapayload.map(item => item[selectedtraveler]);

    // Step2: get all Matching Details List based from the payload
    const allMatchingDetails = [];

    for (const record of combinedatapayload) {
      const detail = record.details || [];
      for (const detailsval of detail) {
        allMatchingDetails.push(detailsval);
      }
    }

    // Step3: Create new participantTaskTimeArray whcih contain the particpant total task travel time
    // loop allSelectedTravelers & allMatchingDetails & mergeparticipantFields and create new array.

    const participantTaskTimeArray = [];

    if (allSelectedTravelers.length > 0 && allMatchingDetails.length > 0) {
      for (const traveler of allSelectedTravelers) {
        // Check if traveler exists in participantsData
        const participantExists = participantsData.some(participant => participant[selectedtraveler] === traveler);

        if (!participantExists) {
          console.log(`Participant not found: ${traveler}`);
          continue;  // Skip to the next traveler
        }
        for (const detail of allMatchingDetails) {
          for (const fieldMap of mergeparticipantFields) {
            const participantField = fieldMap.ZPARTCIPANTFILED;
            const timeField = fieldMap.ZPARTTRVLTIMEFILED;
            const zdetailDate = detail[ZDATERANGE];
            if (detail[participantField] === traveler) {
              const ZDETAILTASKTIMEVAL = detail[timeField] || "00:00";
              // NEWPARTITASKTIME = (traveltime to task + task duration) 
              const NEWPARTITASKTIME = this.addTimeStrings(ZDETAILTASKTIMEVAL, detail[durationField]);
              if (NEWPARTITASKTIME !== "00:00") {
                // Check if record already exists
                const existing = participantTaskTimeArray.find(
                  item => item.USERLOGIN === traveler && item.DATE.startsWith(zdetailDate)
                );

                if (existing) {
                  // Add times together
                  existing.PARTITASKTIME = this.addTimeStrings(existing.PARTITASKTIME, NEWPARTITASKTIME);
                  existing.PARTCIPANTCOUNT = existing.PARTCIPANTCOUNT + 1;
                } else {
                  // Push new record
                  participantTaskTimeArray.push({
                    USERLOGIN: traveler,
                    DATE: zdetailDate,
                    PARTITASKTIME: NEWPARTITASKTIME,
                    PARTCIPANTCOUNT: 1
                  });
                }
              }
            }
          }
        }
      }
    }
    console.log("participantTaskTimeArray", participantTaskTimeArray);
    return participantTaskTimeArray;
  }

  // Add this function to calcualte the travel time for participnats
  // Main Logic:
  // For First task - recalcualte the travel time from home to task location
  // For second task , if the particicpant exist in previous task , the particicpant travel time = task travel time,
  // else recalcuate  the travle time from particicpant home to second task.
  // If the particualter task is the last task for the participant,Find the retrun travel time for the participant from that task.

  static async calculateParticipantSchedule(
    agent,
    tskTravelTimeField,
    selectedtraveler,
    mapVisibilityDate,
    ZDATERANGE,
    participantsData,
    mergeparticipantFields,
    checkIsTravelTimeCalculate,
    isrecordModifiedCheck,
    selectedSourceMapname
  ) {
    if (!agent?.coordinates || !agent.details?.length) {
      return { ...agent, totalTime: 0, finalTravelTime: 0 };
    }
    if (!participantsData || !Array.isArray(participantsData)) {
      console.error("No participant data provided for travel time calculation");
      return { ...agent }; // Return a safe copy
    }
    // Build participant lookup
    const participantsMap = {};
    participantsData.forEach(participant => {
      if (participant.USERLOGIN) {
        participantsMap[participant.USERLOGIN] = participant;
      }
    });

    // Filter relevant details by date
    const dateDetails = agent.details.filter(detail =>
      detail[ZDATERANGE]?.startsWith(mapVisibilityDate)
    );

    if (dateDetails.length === 0) {
      return { ...agent }; // Return unchanged copy
    }

    const activeParticipants = {};
    const updatedDetails = [...agent.details]; // Copy all details up front
    for (let i = 0; i < updatedDetails.length; i++) {
      const detail = updatedDetails[i];

      if (!detail[ZDATERANGE]?.startsWith(mapVisibilityDate)) continue;

      if (isrecordModifiedCheck) {
        detail.recordchange = true;
      }

      for (let j = 0; j < mergeparticipantFields.length; j++) {
        const { ZPARTCIPANTFILED, ZPARTTRVLTIMEFILED } = mergeparticipantFields[j];

        const participantLogin = detail[ZPARTCIPANTFILED];
        const participant = participantsMap[participantLogin];

        if (!participantLogin) {
          detail[ZPARTTRVLTIMEFILED] = "00:00";
          continue;
        }

        if (!participant) {
          console.error(`Participant ${participantLogin} not found in participantsData`);
          detail[ZPARTTRVLTIMEFILED] = "00:00";
          continue;
        }

        if (!activeParticipants[participantLogin]) {
          let participantCoordinates = null;
          let partiFinalTravelTime = null;

          if (participant.ZGEM_POSTCODE) {
            console.log("calling API for participant:", participantLogin);
            participantCoordinates = await this.getCoordinates(participant.ZGEM_POSTCODE);
            if (!participantCoordinates) {
              console.warn(`Failed to geocode ${participant.ZGEM_POSTCODE} for ${participantLogin}`);
              participantCoordinates = agent.coordinates;
            }
          } else {
            console.error(`No postcode for participant ${participantLogin}`);
            participantCoordinates = agent.coordinates;
          }
          // Calcualte the travel time from participant home to task location
          if (participantCoordinates && detail.coordinates) {

            // if the task contain POI, first get the travel time from participant home to POI location,
            // then from POI to task location
            if (detail && Array.isArray(detail.pointsOfInterest) &&
              detail.pointsOfInterest.length > 0 &&
              detail.pointsOfInterest[0].coordinates
            ) {
              const poiTravelTime = await this.calculateTravelTime(
                participantCoordinates,
                detail.pointsOfInterest[0].coordinates
              );
              const partitravelTime = await this.calculateTravelTime(
                detail.pointsOfInterest[0].coordinates,
                detail.coordinates);

              partiFinalTravelTime = (poiTravelTime + partitravelTime);
            }
            else { // If no POI , get the travel time from home to task location
              const travelTime = await this.calculateTravelTime(
                participantCoordinates,
                detail.coordinates
              );
              partiFinalTravelTime = travelTime;
            }
            // Assign the travel time to Participant travel time field in the payload
            detail[ZPARTTRVLTIMEFILED] = this.formatTime(partiFinalTravelTime);
          } else {
            detail[ZPARTTRVLTIMEFILED] = detail[tskTravelTimeField] || "00:00";
          }
        } else {
          // if participant already active in previous task- use particapnt travel time = current task travel time
          detail[ZPARTTRVLTIMEFILED] = detail[tskTravelTimeField] || "00:00";
        }
        // Mark this participant as active
        activeParticipants[participantLogin] = { lastTaskIndex: i };
      }
    }

    // Find the return travel time for the participants
    // Loop the updatedDetails and check participants in each task
    // Check if participant exists in any LATER task, if exist continute
    // If not exist, then the find the retrun travel time for the participant
    // Add the existingTravelTime +  returnTravelTimeStr = totalTravelTimeTime
    for (let i = 0; i < updatedDetails.length; i++) {
      const detail = updatedDetails[i];
      if (!detail[ZDATERANGE]?.startsWith(mapVisibilityDate)) continue;
      // Reset the array fresh for this detail
      detail.participantFinalTrvltime = [];


      for (let j = 0; j < mergeparticipantFields.length; j++) {
        const { ZPARTCIPANTFILED, ZPARTTRVLTIMEFILED } = mergeparticipantFields[j];

        const participantLogin = detail[ZPARTCIPANTFILED];
        if (!participantLogin) continue;

        const participant = participantsMap[participantLogin];
        if (!participant) {
          console.error(`Participant ${participantLogin} not found in participantsData`);
          continue;
        }

        // Check if participant exists in any LATER task
        const existsInLaterTasks = updatedDetails.some((nextDetail, nextIdx) => {
          if (nextIdx <= i || !nextDetail[ZDATERANGE]?.startsWith(mapVisibilityDate)) return false;
          return mergeparticipantFields.some(field =>
            nextDetail[field.ZPARTCIPANTFILED] === participantLogin
          );
        });

        if (existsInLaterTasks) continue; // Participant has more tasks, skip

        // Calculate return travel time
        let participantCoordinates = null;

        if (participant.ZGEM_POSTCODE) {
          participantCoordinates = await this.getCoordinates(participant.ZGEM_POSTCODE);
          if (!participantCoordinates) {
            console.warn(`Failed to geocode ${participant.ZGEM_POSTCODE} for return trip of ${participantLogin}`);
            participantCoordinates = agent.coordinates;
          }
        } else {
          console.warn(`No postcode for participant ${participantLogin}, using agent coordinates`);
          participantCoordinates = agent.coordinates;
        }

        if (participantCoordinates && detail.coordinates) {
          // Get the return travel time
          const returnTravelTime = await this.calculateTravelTime(
            detail.coordinates,
            participantCoordinates
          );
          const returnTravelTimeStr = this.formatTime(returnTravelTime);
          // get the participant existing travel Time for the  task
          const existingTravelTime = detail[ZPARTTRVLTIMEFILED] || "00:00";
          // Total travel time  for the participant
          const totalTravelTimeTime = this.addTimeStrings(existingTravelTime, returnTravelTimeStr);

          console.log("Adding return travel time for", participantLogin, {
            forward: existingTravelTime,
            return: returnTravelTimeStr,
            total: totalTravelTimeTime
          });
          // Update back the totalTravlTime to the  participant travltime field
          detail[ZPARTTRVLTIMEFILED] = totalTravelTimeTime;

          // Create a new record in participantFinalTrvltime
          detail.participantFinalTrvltime.push({
            MAPNAME: selectedSourceMapname,
            TRAVELDATE: detail.CURDATE,
            TRAVELER: participantLogin,
            TRAVELTIME: returnTravelTimeStr,
            TRANSID: 0,
            TRAVLRTYPE: "Participant",
            TEAMLEADER: detail[selectedtraveler],
            USERPOI: ""
          });
        } else {
          console.warn(`Missing coordinates for return travel calculation for ${participantLogin}`);
        }
      }
    }
    return {
      ...agent,
      details: updatedDetails
    };
  }

  // Add this function to calcualte the task duration with participants 
  static async calculatetaskdurationwithparticipants(agent, taskdurationField, participantsData,
    travelerFieldName, mapVisibilityDate, ZDATERANGE, mergeparticipantFields, isrecordModifiedCheck) {

    const agentCopy = { ...agent };

    if (!agentCopy?.coordinates || !agentCopy.details?.length) {
      return { ...agentCopy, totalTime: 0, finalTravelTime: 0 };
    }

    if (!participantsData || !Array.isArray(participantsData)) {
      console.error('No participant data provided for travel time calculation');
      return agentCopy;
    }

    const dateDetails = agentCopy.details.filter(detail =>
      detail[ZDATERANGE]?.startsWith(mapVisibilityDate)
    );

    if (dateDetails.length === 0) {
      return agentCopy;
    }

    /********* START the task duration calculation ******/
    // This logc will work for both add the participnats and remove the participants.
    // When Add Participant: Task Duration will reduce
    // When Remove Participant: Task Duration become old value

    dateDetails.forEach((detail, agentindex) => {
      try {
        // Step1: Get Total member Points
        let totalMemberPoints = 0.00;
        mergeparticipantFields.forEach(field => {
          const partcipantFieldName = field.ZPARTCIPANTFILED;
          const participantName = detail[partcipantFieldName];

          if (participantName) {
            const participantrecord = participantsData.find(partiData => partiData.USERLOGIN === participantName);
            if (participantrecord) {
              totalMemberPoints += participantrecord.ZGEM_MEMBERPOINT || 0;
            }
          }
        });
        // Step2: Get new task Duration value
        const zTotalTeamPoint = (agent.ZGEM_TLPOINT + parseFloat(totalMemberPoints.toFixed(2)));
        const labourPoints = detail.ZCLA_LABOURPOINTS || 0;

        const durationInSeconds = labourPoints * (30 / zTotalTeamPoint);
        //const durationInSeconds = labourPoints * (90 / zTotalTeamPoint);
        const durationInHours = parseFloat((durationInSeconds / 3600).toFixed(2));

        const oldtaskdurationValue = detail[taskdurationField];
        const newtaskdurationValue = this.formatTime(durationInHours.toFixed(2));
        const isValidTime = /^[0-9]{1,2}:[0-9]{2}$/.test(newtaskdurationValue);

        console.log("oldtaskdurationValue", oldtaskdurationValue, newtaskdurationValue, isValidTime, durationInHours.toFixed(2))

        // Step3: Update the new task Duration value into task payload field
        if (newtaskdurationValue && isValidTime) {
          detail[taskdurationField] = newtaskdurationValue;
        } else {
          detail[taskdurationField] = oldtaskdurationValue;
          console.warn(`Invalid formatted time for task "${detail.SUBJECT}". Reverting to old value: ${oldtaskdurationValue}`);
        }

        if (isrecordModifiedCheck) {
          detail.recordchange = true;
        }
        console.log("each taskVALUE:", detail.SUBJECT);
        console.log("totalMemberPoints:", totalMemberPoints, agent.ZGEM_TLPOINT, zTotalTeamPoint);
        console.log("labourPoints:", detail.ZCLA_LABOURPOINTS);
        console.log("durationInSeconds:", durationInSeconds);
        console.log("durationInHours:", durationInHours);
        console.log("Final Time Assigned:", detail[taskdurationField]);
      } catch (error) {
        console.error(`Error calculating task duration for task: ${detail.SUBJECT}`, error);
        // Do not assign anything if there's an error, to preserve old value
      }
    });
    /********* END task duration calculation ******/
    return agentCopy;
  }

  static async recalculateTravelTimeAfterDragDropCalendar(sourceAgent, destAgent, sourcedetailIndex, sourceAgentIndex,
    destinationdetailindex, destAgentIndex, durationField, fielduserstartTime, tskTravelTimeField,
    finalTravelTimeData, selectedtraveler, mapVisibilityDate, ZDATERANGE, timeATPOI, formattedDateSource,
    TopmapVisibilityDate, selectedSourceMapname) {
    // Handle same agent case
    if (sourceAgentIndex === destAgentIndex) {
      const updatedAgent = await this.recalculateSingleAgentTravelTimeCalendar(
        sourceAgent,
        sourcedetailIndex,
        destinationdetailindex,
        durationField,
        fielduserstartTime,
        tskTravelTimeField,
        finalTravelTimeData,
        selectedtraveler,
        mapVisibilityDate,
        ZDATERANGE,
        timeATPOI,
        formattedDateSource,
        TopmapVisibilityDate,
        selectedSourceMapname
      );
      return { sourceAgent: updatedAgent, destAgent: updatedAgent };
    }
    else {
      // Handle different agents case
      // if (sourcedetailIndex >= 0) {
      const updatedSourceAgent = await this.recalculateSourceAgentTravelTimeCalendar(
        sourceAgent,
        sourcedetailIndex,
        durationField,
        fielduserstartTime,
        tskTravelTimeField,
        finalTravelTimeData,
        selectedtraveler,
        mapVisibilityDate,
        ZDATERANGE,
        timeATPOI,
        TopmapVisibilityDate,
        selectedSourceMapname
      );
      //   return { sourceAgent: updatedSourceAgent };
      // }

      // if (destinationdetailindex >= 0) {
      const updatedDestAgent = await this.recalculateDestAgentTravelTimeCalendar(
        destAgent,
        destinationdetailindex,
        durationField,
        fielduserstartTime,
        tskTravelTimeField,
        finalTravelTimeData,
        selectedtraveler,
        mapVisibilityDate,
        ZDATERANGE,
        timeATPOI,
        TopmapVisibilityDate,
        selectedSourceMapname
      );
      //   return { destAgent: updatedDestAgent };
      // }
      return { sourceAgent: updatedSourceAgent, destAgent: updatedDestAgent };
    }
  }

  static async recalculateSingleAgentTravelTimeCalendar(agent, sourceIndex, destIndex, durationField,
    fielduserstartTime, tskTravelTimeField, finalTravelTimeData, selectedtraveler, mapVisibilityDate,
    ZDATERANGE, timeATPOI, formattedDateSource, TopmapVisibilityDate, selectedSourceMapname) {
    if (!agent?.coordinates || !agent.details?.length) {
      return agent;
    }
    // Handle tasks for different dates separately
    if (formattedDateSource !== mapVisibilityDate) {
      // Get source date tasks
      const sourceDateTasks = agent.details.filter(detail =>
        detail[ZDATERANGE].startsWith(formattedDateSource)
      );
      // Get destination date tasks
      const destDateTasks = agent.details.filter(detail =>
        detail[ZDATERANGE].startsWith(mapVisibilityDate)
      );

      if (sourceDateTasks.length === 0 && destDateTasks.length === 0) {
        return agent;
      }

      const sourceIndicesToRecalculate = new Set();
      const destIndicesToRecalculate = new Set();

      // Process source date indices
      if (sourceIndex < sourceDateTasks.length) {
        sourceIndicesToRecalculate.add(sourceIndex);
      }

      // Process destination date indices
      if (destIndex < destDateTasks.length) {
        destIndicesToRecalculate.add(destIndex);
        if (destIndex + 1 < destDateTasks.length) {
          destIndicesToRecalculate.add(destIndex + 1);
        }
      }
      // Process source date tasks
      if (sourceDateTasks.length > 0) {
        agent = await this.processAgentTravelTimesCalender(
          agent,
          sourceIndicesToRecalculate,
          sourceDateTasks,
          durationField,
          tskTravelTimeField,
          finalTravelTimeData,
          selectedtraveler,
          formattedDateSource,
          ZDATERANGE,
          timeATPOI,
          TopmapVisibilityDate,
          selectedSourceMapname
        );
      }
      // Process destination date tasks
      if (destDateTasks.length > 0) {
        agent = await this.processAgentTravelTimesCalender(
          agent,
          destIndicesToRecalculate,
          destDateTasks,
          durationField,
          tskTravelTimeField,
          finalTravelTimeData,
          selectedtraveler,
          mapVisibilityDate,
          ZDATERANGE,
          timeATPOI,
          TopmapVisibilityDate,
          selectedSourceMapname
        );
      }

      return agent;
    }

    // Original logic for same date
    const tasksForDate = agent.details.filter(detail =>
      detail[ZDATERANGE].startsWith(mapVisibilityDate)
    );

    if (tasksForDate.length === 0) {
      return agent;
    }

    const indicesToRecalculate = new Set();

    // Add source position and next task
    if (sourceIndex < tasksForDate.length) {
      indicesToRecalculate.add(sourceIndex);
      if (sourceIndex + 1 < tasksForDate.length) {
        indicesToRecalculate.add(sourceIndex + 1);
      }
    }

    // Add destination position and next task
    indicesToRecalculate.add(destIndex);
    if (destIndex + 1 < tasksForDate.length) {
      indicesToRecalculate.add(destIndex + 1);
    }

    return await this.processAgentTravelTimesCalender(
      agent,
      indicesToRecalculate,
      tasksForDate,
      durationField,
      tskTravelTimeField,
      finalTravelTimeData,
      selectedtraveler,
      mapVisibilityDate,
      ZDATERANGE,
      timeATPOI,
      TopmapVisibilityDate,
      selectedSourceMapname
    );
  }

  static async recalculateSourceAgentTravelTimeCalendar(agent, sourceIndex, durationField, fielduserstartTime,
    tskTravelTimeField, finalTravelTimeData, selectedtraveler, mapVisibilityDate,
    ZDATERANGE, timeATPOI, TopmapVisibilityDate, selectedSourceMapname) {
    if (!agent?.coordinates || !agent.details?.length) {
      return agent;
    }

    const tasksForDate = agent.details.filter(detail =>
      detail[ZDATERANGE].startsWith(mapVisibilityDate)
    );

    if (tasksForDate.length === 0) {
      return agent;
    }

    const indicesToRecalculate = new Set();

    // Only recalculate the source position for source agent
    if (sourceIndex < tasksForDate.length) {
      indicesToRecalculate.add(sourceIndex);
    }

    return await this.processAgentTravelTimesCalender(
      agent,
      indicesToRecalculate,
      tasksForDate,
      durationField,
      tskTravelTimeField,
      finalTravelTimeData,
      selectedtraveler,
      mapVisibilityDate, // source/dest date
      ZDATERANGE,
      timeATPOI,
      TopmapVisibilityDate,  // currently selected date in sidebar,
      selectedSourceMapname
    );
  }

  static async recalculateDestAgentTravelTimeCalendar(agent, destIndex, durationField, fielduserstartTime,
    tskTravelTimeField, finalTravelTimeData, selectedtraveler, mapVisibilityDate,
    ZDATERANGE, timeATPOI, TopmapVisibilityDate, selectedSourceMapname) {
    if (!agent?.coordinates || !agent.details?.length) {
      return agent;
    }

    const tasksForDate = agent.details.filter(detail =>
      detail[ZDATERANGE].startsWith(mapVisibilityDate)
    );
    if (tasksForDate.length === 0) {
      return agent;
    }

    const indicesToRecalculate = new Set();

    // Recalculate destination position and next task
    indicesToRecalculate.add(destIndex);
    if (destIndex + 1 < tasksForDate.length) {
      indicesToRecalculate.add(destIndex + 1);
    }

    return await this.processAgentTravelTimesCalender(
      agent,
      indicesToRecalculate,
      tasksForDate,
      durationField,
      tskTravelTimeField,
      finalTravelTimeData,
      selectedtraveler,
      mapVisibilityDate,
      ZDATERANGE,
      timeATPOI,
      TopmapVisibilityDate,
      selectedSourceMapname
    );
  }

  static async processAgentTravelTimesCalender(agent, indicesToRecalculate, tasksForDate, durationField,
    tskTravelTimeField, finalTravelTimeData, selectedtraveler, mapVisibilityDate,
    ZDATERANGE, timeATPOI, TopmapVisibilityDate, selectedSourceMapname) {
    // Sort indices to process them in order
    let sortedIndices = Array.from(indicesToRecalculate).sort((a, b) => a - b);
    if (tasksForDate.length === 1 && sortedIndices.length === 1 && sortedIndices[0] !== 0) {
      sortedIndices = [0];
    }
    // get alternate travel coordinates
    const coordinatesInfo = this.getValidCoordinates(agent, mapVisibilityDate);

    // Clone and update or append to finalTravelTimeData
    const updatedTravelData = [...finalTravelTimeData];

    // Process each task that needs recalculation
    for (const taskIndex of sortedIndices) {
      const task = tasksForDate[taskIndex];
      let prevCoords;
      if (taskIndex === 0) {
        prevCoords = agent.coordinates;
      } else {
        prevCoords = tasksForDate[taskIndex - 1].coordinates;
      }

      const taskToUpdate = agent.details.find(detail =>
        detail[ZDATERANGE] === task[ZDATERANGE] &&
        Object.keys(detail).every(key => detail[key] === task[key])
      );
      if (taskToUpdate) {
        // Calculate travel time to detail POI if exists
        if (task.pointsOfInterest?.length > 0) {
          const poiTravelTime = await this.calculateTravelTime(
            prevCoords,
            task.pointsOfInterest[0].coordinates
          );
          const taskTravelTime = await this.calculateTravelTime(
            task.pointsOfInterest[0].coordinates,
            task.coordinates
          );
          taskToUpdate.poiTravelTime = (poiTravelTime + this.parseTime(timeATPOI));
          taskToUpdate.travelTimeTo = (taskTravelTime + poiTravelTime + this.parseTime(timeATPOI));
        } else {
          // Direct travel to task
          const travelTime = await this.calculateTravelTime(prevCoords, task.coordinates);
          taskToUpdate.travelTimeTo = travelTime;
          taskToUpdate.poiTravelTime = 0;  // Reset POI travel time if no POI
        }
        taskToUpdate[tskTravelTimeField] = this.minutesToTimeString(Math.round(taskToUpdate.travelTimeTo * 60));
        // taskToUpdate.recordchange = true;
      }
    }

    // Only calculate totalTime if the date we're processing matches mapVisibilityDate

    //let shouldUpdateTotalTime = mapVisibilityDate === TopmapVisibilityDate;
    // const totalTime = shouldUpdateTotalTime ? tasksForDate.reduce((sum, detail) => {
    //   return sum + (detail.travelTimeTo || 0) + this.parseTime(detail[durationField]);
    // }, 0) : agent.totalTime || 0;  // Keep existing totalTime if not updating

    // Calculate total time including Header POI travel times
    const totalTime = tasksForDate.reduce((sum, detail) => {
      return sum + (detail.travelTimeTo || 0) + this.parseTime(detail[durationField]);
    }, 0);  // Keep existing totalTime if not updating

    // Calculate final travel time considering agent's POI
    let finalTravelTime = 0;
    const lastTask = tasksForDate[tasksForDate.length - 1];

    if (agent.pointsOfInterest?.length > 0) {
      const matchingPOI = agent.pointsOfInterest.find(poi =>
        poi.POIASSIGNDATE?.startsWith(mapVisibilityDate)
      );

      if (matchingPOI) {
        const poiTravelTime = await this.calculateTravelTime(
          lastTask.coordinates,
          matchingPOI.coordinates
        );
        const homeTravelTime = await this.calculateTravelTime(
          matchingPOI.coordinates,
          coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates
        );
        finalTravelTime = (poiTravelTime + homeTravelTime + this.parseTime(timeATPOI));

      } else {
        finalTravelTime = await this.calculateTravelTime(lastTask.coordinates,
          coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates);
      }
    } else {
      finalTravelTime = await this.calculateTravelTime(lastTask.coordinates,
        coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates);
    }

    // Final TravelTime update Logic
    const matchIndex = updatedTravelData.findIndex(data =>
      data.TRAVELER === agent[selectedtraveler] &&
      data.MAPNAME === selectedSourceMapname &&
      data.TRAVELDATE.startsWith(mapVisibilityDate) &&
      data.TRAVLRTYPE === "TeamLeader"
    );

    if (matchIndex !== -1) {
      updatedTravelData[matchIndex] = {
        ...updatedTravelData[matchIndex],
        TRAVELTIME: this.formatTime(finalTravelTime)
      };
    } else {
      updatedTravelData.push({
        MAPNAME: selectedSourceMapname,
        TRAVELDATE: mapVisibilityDate,
        TRAVELER: agent[selectedtraveler],
        TRAVELTIME: this.formatTime(finalTravelTime),
        TRANSID: 0,
        USERPOI: "",
        TRAVLRTYPE: "TeamLeader",
        TEAMLEADER: agent[selectedtraveler]
      });
    }
    //  Return updated agent with filtered records for the current traveler
    return {
      ...agent,
      totalTime: totalTime + finalTravelTime,
      finalTravelTime: updatedTravelData.filter(data =>
        data.TRAVELER === agent[selectedtraveler] &&
        data.TRAVLRTYPE === "TeamLeader"
      )
    };
  }

  static async recalculateTravelTimeAfterDragDrop(sourceAgent, destAgent, sourcedetailIndex, sourceAgentIndex,
    destinationdetailindex, destAgentIndex, durationField, fielduserstartTime, tskTravelTimeField,
    finalTravelTimeData, selectedtraveler, mapVisibilityDate, ZDATERANGE, timeATPOI, selectedSourceMapname) {
    if (durationField === "" || durationField === undefined || fielduserstartTime === "") {
      return { sourceAgent: sourceAgent, destAgent: destAgent };
    }
    // Handle same agent case
    if (sourceAgentIndex === destAgentIndex) {
      const updatedAgent = await this.recalculateSingleAgentTravelTime(
        sourceAgent,
        sourcedetailIndex,
        destinationdetailindex,
        durationField,
        fielduserstartTime,
        tskTravelTimeField,
        finalTravelTimeData,
        selectedtraveler,
        mapVisibilityDate,
        ZDATERANGE,
        timeATPOI,
        selectedSourceMapname
      );
      return { sourceAgent: updatedAgent, destAgent: updatedAgent };
    }

    // Handle different agents case
    const updatedSourceAgent = await this.recalculateSourceAgentTravelTime(
      sourceAgent,
      sourcedetailIndex,
      durationField,
      fielduserstartTime,
      tskTravelTimeField,
      finalTravelTimeData,
      selectedtraveler,
      mapVisibilityDate,
      ZDATERANGE, timeATPOI, selectedSourceMapname
    );

    const updatedDestAgent = await this.recalculateDestAgentTravelTime(
      destAgent,
      destinationdetailindex,
      durationField,
      fielduserstartTime,
      tskTravelTimeField,
      finalTravelTimeData,
      selectedtraveler,
      mapVisibilityDate,
      ZDATERANGE, timeATPOI, selectedSourceMapname
    );

    return { sourceAgent: updatedSourceAgent, destAgent: updatedDestAgent };
  }

  static async recalculateSingleAgentTravelTime(agent, sourceIndex, destIndex, durationField,
    fielduserstartTime, tskTravelTimeField, finalTravelTimeData, selectedtraveler,
    mapVisibilityDate, ZDATERANGE, timeATPOI, selectedSourceMapname) {

    if (!agent?.coordinates || !agent.details?.length) {
      return agent;
    }

    const tasksForDate = agent.details.filter(detail =>
      detail[ZDATERANGE].startsWith(mapVisibilityDate)
    );

    if (tasksForDate.length === 0) {
      return agent;
    }

    const indicesToRecalculate = new Set();

    // Add source position and next task
    if (sourceIndex < tasksForDate.length) {
      indicesToRecalculate.add(sourceIndex);
      if (sourceIndex + 1 < tasksForDate.length) {
        indicesToRecalculate.add(sourceIndex + 1);
      }
    }

    // Add destination position and next task
    indicesToRecalculate.add(destIndex);
    if (destIndex + 1 < tasksForDate.length) {
      indicesToRecalculate.add(destIndex + 1);
    }

    return await this.processAgentTravelTimes(
      agent,
      indicesToRecalculate,
      tasksForDate,
      durationField,
      tskTravelTimeField,
      finalTravelTimeData,
      selectedtraveler,
      mapVisibilityDate,
      ZDATERANGE,
      timeATPOI, selectedSourceMapname
    );
  }

  static async recalculateSourceAgentTravelTime(agent, sourceIndex, durationField, fielduserstartTime,
    tskTravelTimeField, finalTravelTimeData, selectedtraveler, mapVisibilityDate, ZDATERANGE,
    timeATPOI, selectedSourceMapname) {
    if (!agent?.coordinates || !agent.details?.length) {
      return agent;
    }

    const tasksForDate = agent.details.filter(detail =>
      detail[ZDATERANGE].startsWith(mapVisibilityDate)
    );

    if (tasksForDate.length === 0) {
      return agent;
    }

    const indicesToRecalculate = new Set();

    // Only recalculate the source position for source agent
    if (sourceIndex < tasksForDate.length) {
      indicesToRecalculate.add(sourceIndex);
    }

    return await this.processAgentTravelTimes(
      agent,
      indicesToRecalculate,
      tasksForDate,
      durationField,
      tskTravelTimeField,
      finalTravelTimeData,
      selectedtraveler,
      mapVisibilityDate,
      ZDATERANGE,
      timeATPOI,
      selectedSourceMapname
    );
  }

  static async recalculateDestAgentTravelTime(agent, destIndex, durationField, fielduserstartTime,
    tskTravelTimeField, finalTravelTimeData, selectedtraveler, mapVisibilityDate,
    ZDATERANGE, timeATPOI, selectedSourceMapname) {
    if (!agent?.coordinates || !agent.details?.length) {
      return agent;
    }

    const tasksForDate = agent.details.filter(detail =>
      detail[ZDATERANGE].startsWith(mapVisibilityDate)
    );
    if (tasksForDate.length === 0) {
      return agent;
    }

    const indicesToRecalculate = new Set();

    // Recalculate destination position and next task
    indicesToRecalculate.add(destIndex);
    if (destIndex + 1 < tasksForDate.length) {
      indicesToRecalculate.add(destIndex + 1);
    }

    return await this.processAgentTravelTimes(
      agent,
      indicesToRecalculate,
      tasksForDate,
      durationField,
      tskTravelTimeField,
      finalTravelTimeData,
      selectedtraveler,
      mapVisibilityDate,
      ZDATERANGE,
      timeATPOI, selectedSourceMapname
    );
  }

  static async processAgentTravelTimes(
    agent,
    indicesToRecalculate,
    tasksForDate,
    durationField,
    tskTravelTimeField,
    finalTravelTimeData,
    selectedtraveler,
    mapVisibilityDate,
    ZDATERANGE,
    timeATPOI,
    selectedSourceMapname
  ) {
    let sortedIndices = Array.from(indicesToRecalculate).sort((a, b) => a - b);
    if (tasksForDate.length === 1 && sortedIndices.length === 1 && sortedIndices[0] !== 0) {
      sortedIndices = [0];
    }

    const coordinatesInfo = this.getValidCoordinates(agent, mapVisibilityDate);

    // Clone and update or append to finalTravelTimeData
    const updatedTravelData = [...finalTravelTimeData];

    // Update travel times for selected tasks
    for (const taskIndex of sortedIndices) {
      const task = tasksForDate[taskIndex];
      const prevCoords = taskIndex === 0
        ? agent.coordinates
        : tasksForDate[taskIndex - 1].coordinates;

      const taskToUpdate = agent.details.find(detail =>
        detail[ZDATERANGE] === task[ZDATERANGE] &&
        Object.keys(detail).every(key => detail[key] === task[key])
      );

      if (taskToUpdate) {
        if (task.pointsOfInterest?.length > 0) {
          const poiTravelTime = await this.calculateTravelTime(prevCoords, task.pointsOfInterest[0].coordinates);
          const taskTravelTime = await this.calculateTravelTime(task.pointsOfInterest[0].coordinates, task.coordinates);
          taskToUpdate.poiTravelTime = poiTravelTime + this.parseTime(timeATPOI);
          taskToUpdate.travelTimeTo = taskTravelTime + poiTravelTime + this.parseTime(timeATPOI);
        } else {
          const travelTime = await this.calculateTravelTime(prevCoords, task.coordinates);
          taskToUpdate.travelTimeTo = travelTime;
          taskToUpdate.poiTravelTime = 0;
        }
        taskToUpdate[tskTravelTimeField] = this.minutesToTimeString(Math.round(taskToUpdate.travelTimeTo * 60));
      }
    }

    // Calculate total time from tasks
    const totalTime = tasksForDate.reduce((sum, detail) =>
      sum + (detail.travelTimeTo || 0) + this.parseTime(detail[durationField]), 0
    );

    // Final travel time calculation
    const lastTask = tasksForDate[tasksForDate.length - 1];
    let finalTravelTime = 0;

    const matchingPOI = agent.pointsOfInterest?.find(poi =>
      poi.POIASSIGNDATE?.startsWith(mapVisibilityDate)
    );

    if (matchingPOI) {
      const poiTravelTime = await this.calculateTravelTime(lastTask.coordinates, matchingPOI.coordinates);
      const homeTravelTime = await this.calculateTravelTime(
        matchingPOI.coordinates,
        coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates
      );
      finalTravelTime = poiTravelTime + homeTravelTime + this.parseTime(timeATPOI);
    } else {
      finalTravelTime = await this.calculateTravelTime(
        lastTask.coordinates,
        coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates
      );
    }

    // Final TravelTime update Logic
    const matchIndex = updatedTravelData.findIndex(data =>
      data.TRAVELER === agent[selectedtraveler] &&
      data.MAPNAME === selectedSourceMapname &&
      data.TRAVELDATE.startsWith(mapVisibilityDate) &&
      data.TRAVLRTYPE === "TeamLeader"
    );

    if (matchIndex !== -1) {
      updatedTravelData[matchIndex] = {
        ...updatedTravelData[matchIndex],
        TRAVELTIME: this.formatTime(finalTravelTime)
      };
    } else {
      updatedTravelData.push({
        MAPNAME: selectedSourceMapname,
        TRAVELDATE: mapVisibilityDate,
        TRAVELER: agent[selectedtraveler],
        TRAVELTIME: this.formatTime(finalTravelTime),
        TRANSID: 0,
        USERPOI: "",
        TRAVLRTYPE: "TeamLeader",
        TEAMLEADER: agent[selectedtraveler]
      });
    }
    //  Return updated agent with filtered records for the current traveler
    return {
      ...agent,
      totalTime: totalTime + finalTravelTime,
      finalTravelTime: updatedTravelData.filter(data =>
        data.TRAVELER === agent[selectedtraveler] &&
        data.TRAVLRTYPE === "TeamLeader"
      )
    };
  }


  static async calculatetravelTimeAfterPayloadCompare(
    agent,
    tasksForDate,
    durationField,
    tskTravelTimeField,
    finalTravelTimeData,
    selectedtraveler,
    mapVisibilityDate,
    ZDATERANGE,
    timeATPOI,
    selectedSourceMapname
  ) {
    let sortedIndices = [];
    if (tasksForDate.length === 1) {
      sortedIndices = [0];
    }
    else {
      // include all indices from 0 to (tasksForDate.length - 1)
      sortedIndices = Array.from({ length: tasksForDate.length }, (_, i) => i);
    }

    const coordinatesInfo = this.getValidCoordinates(agent, mapVisibilityDate);
    // Clone and update or append to finalTravelTimeData
    const updatedTravelData = agent.finalTravelTime;

    // Update travel times for selected tasks
    for (const taskIndex of sortedIndices) {
      const task = tasksForDate[taskIndex];
      const prevCoords = taskIndex === 0
        ? agent.coordinates
        : tasksForDate[taskIndex - 1].coordinates;

      if (task) {
        if (agent.ZGEM_DUMMYUSER !== 'Y') {
          if (task.pointsOfInterest?.length > 0) {
            const poiTravelTime = await this.calculateTravelTime(prevCoords, task.pointsOfInterest[0].coordinates);
            const taskTravelTime = await this.calculateTravelTime(task.pointsOfInterest[0].coordinates, task.coordinates);
            task.poiTravelTime = poiTravelTime + this.parseTime(timeATPOI);
            const travelTime = taskTravelTime + poiTravelTime + this.parseTime(timeATPOI)
            task.travelTimeTo = travelTime;
          } else {
            const travelTime = await this.calculateTravelTime(prevCoords, task.coordinates);
            task.travelTimeTo = travelTime;
            task.poiTravelTime = 0;
          }
          // update task travel time back to task payload field
          const tskTrvlVal = this.minutesToTimeString(Math.round(task.travelTimeTo * 60));
          task[tskTravelTimeField] = tskTrvlVal; 
        }
        task.recordchange = true;
      }
    }

    // Calculate total time from tasks
    const totalTime = tasksForDate.reduce((sum, detail) =>
      sum + (detail.travelTimeTo || 0) + this.parseTime(detail[durationField]), 0
    );

    // Final travel time calculation
    const lastTask = tasksForDate[tasksForDate.length - 1];
    let finalTravelTime = 0;

    const matchingPOI = agent.pointsOfInterest?.find(poi =>
      poi.POIASSIGNDATE?.startsWith(mapVisibilityDate)
    );

    if (agent.ZGEM_DUMMYUSER !== 'Y') {
      if (matchingPOI) {
        const poiTravelTime = await this.calculateTravelTime(lastTask.coordinates, matchingPOI.coordinates);
        const homeTravelTime = await this.calculateTravelTime(
          matchingPOI.coordinates,
          coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates
        );
        finalTravelTime = poiTravelTime + homeTravelTime + this.parseTime(timeATPOI);
      } else {
        finalTravelTime = await this.calculateTravelTime(
          lastTask.coordinates,
          coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates
        );
      }

      // Final TravelTime update Logic
      const matchIndex = updatedTravelData.findIndex(data =>
        data.TRAVELER === agent[selectedtraveler] &&
        data.MAPNAME === selectedSourceMapname &&
        data.TRAVELDATE.startsWith(mapVisibilityDate) &&
        data.TRAVLRTYPE === "TeamLeader"
      );

      if (matchIndex !== -1) {
        updatedTravelData[matchIndex] = {
          ...updatedTravelData[matchIndex],
          TRAVELTIME: this.formatTime(finalTravelTime)
        };
      } else {
        updatedTravelData.push({
          MAPNAME: selectedSourceMapname,
          TRAVELDATE: mapVisibilityDate,
          TRAVELER: agent[selectedtraveler],
          TRAVELTIME: this.formatTime(finalTravelTime),
          TRANSID: 0,
          USERPOI: "",
          TRAVLRTYPE: "TeamLeader",
          TEAMLEADER: agent[selectedtraveler]
        });
      }
    }
    //  Return updated agent with filtered records for the current traveler
    return {
      ...agent,
      totalTime: totalTime + finalTravelTime,
      finalTravelTime: updatedTravelData.filter(data =>
        data.TRAVELER === agent[selectedtraveler] &&
        data.TRAVLRTYPE === "TeamLeader"
      )
    };
  }

  static async calculateAgentFinaltraveltime(
    agent,
    durationField,
    fielduserstartTime,
    tskTravelTimeField,
    finalTravelTimeData,
    selectedtraveler,
    mapVisibilityDate,
    ZDATERANGE,
    timeATPOI,
    selectedUKey,
    selectedSourceMapname
  ) {
    if (!agent?.coordinates || !agent.details?.length || !durationField) {
      return { ...agent, totalTime: 0, finalTravelTimeData: [] };
    }
    const coordinatesInfo = this.getValidCoordinates(agent, mapVisibilityDate);
    let totalTime = 0;

    //const updatedTravelData = [...finalTravelTimeData];
    const updatedTravelData = agent.finalTravelTime;

    // Get filtered details by date
    const dateDetails = agent.details.filter(detail =>
      detail[ZDATERANGE].startsWith(mapVisibilityDate)
    );
    // Determine previousCoords from last task or fallback to agent location
    const previousCoords =
      dateDetails.length > 0
        ? (dateDetails[dateDetails.length - 1].coordinates || agent.coordinates)
        : agent.coordinates;

    // Calculate return travel time
    let finalTravelTime = 0;

    const matchingPOI = agent.pointsOfInterest?.find(poi =>
      poi.POIASSIGNDATE.startsWith(mapVisibilityDate)
    );

    if (matchingPOI) {
      const poiTravelTime = await this.calculateTravelTime(previousCoords, matchingPOI.coordinates);
      const homeTravelTime = await this.calculateTravelTime(
        matchingPOI.coordinates,
        coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates
      );
      finalTravelTime = poiTravelTime + homeTravelTime + this.parseTime(timeATPOI);
    } else {
      finalTravelTime = await this.calculateTravelTime(
        previousCoords,
        coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates
      );
    }
    // Calculate total time from tasks + final travel
    totalTime = dateDetails.reduce((sum, detail) => {
      return sum + (detail.travelTimeTo || 0) + this.parseTime(detail[durationField]);
    }, 0);

    totalTime += finalTravelTime;

    const currentDate = dateDetails.length > 0
      ? dateDetails[0][ZDATERANGE].split("T")[0]
      : mapVisibilityDate;

    // Final TravelTime update Logic
    const matchIndex = updatedTravelData.findIndex(data =>
      data.TRAVELER === agent[selectedtraveler] &&
      data.MAPNAME === selectedSourceMapname &&
      data.TRAVELDATE.slice(0, 10) === currentDate.slice(0, 10) &&
      data.TRAVLRTYPE === "TeamLeader"
    );

    if (matchIndex !== -1) {
      updatedTravelData[matchIndex] = {
        ...updatedTravelData[matchIndex],
        TRAVELTIME: this.formatTime(finalTravelTime),
        USERPOI: selectedUKey
      };
    } else {
      updatedTravelData.push({
        MAPNAME: selectedSourceMapname,
        TRAVELDATE: currentDate,
        TRAVELER: agent[selectedtraveler],
        TRAVELTIME: this.formatTime(finalTravelTime),
        TRANSID: 0,
        USERPOI: selectedUKey,
        TRAVLRTYPE: "TeamLeader",
        TEAMLEADER: agent[selectedtraveler]
      });
    }
    // Return updated agent and filtered relevant travel time entries
    return {
      ...agent,
      details: agent.details, // no updates to details required
      totalTime,
      finalTravelTime: updatedTravelData.filter(
        data => data.TRAVELER === agent[selectedtraveler] &&
          data.TRAVLRTYPE === "TeamLeader"
      )
    };
  }


  static async calculateTravelTimeTo(agent, durationField, fielduserstartTime, tskTravelTimeField,
    finalTravelTimeData, selectedtraveler, mapVisibilityDate, ZDATERANGE,
    callFrom, selectedTaskIndex, timeATPOI, selectedSourceMapname) {
    var matchedDetails = [];
    if (!agent?.coordinates || !agent.details?.length || durationField === "" || durationField === undefined) {
      return agent;
    }
    // Clone and update or append to finalTravelTimeData
    const updatedTravelData = [...finalTravelTimeData];

    // get alternate travel coordinates
    const coordinatesInfo = this.getValidCoordinates(agent, mapVisibilityDate);

    const tasksForDate = agent.details.filter(detail =>
      detail[ZDATERANGE].startsWith(mapVisibilityDate)
    );

    if (tasksForDate.length === 0) {
      return agent;
    }

    if (callFrom === "LeftSidebar") {
      // Find tasks with empty travelTimeTo
      matchedDetails = tasksForDate.filter(task => task.travelTimeTo === 0);
      if (matchedDetails.length === 0) {
        return agent;
      }
    }
    else { //mapandtrack
      // get the POI added task
      matchedDetails = [agent.details[selectedTaskIndex]];
    }
    // Keep track of tasks that need recalculation
    const tasksToRecalculate = new Set();

    // First get indices of all empty travel tasks
    const emptyTravelIndices = matchedDetails.map(task =>
      tasksForDate.findIndex(t => t === task)
    );

    // For each empty travel time task
    for (const emptyIndex of emptyTravelIndices) {
      tasksToRecalculate.add(emptyIndex);

      // Find the next non-empty travel time task
      let nextIndex = emptyIndex + 1;
      while (nextIndex < tasksForDate.length) {
        tasksToRecalculate.add(nextIndex);
        // If this is also an empty travel time task, continue to the next
        if (emptyTravelIndices.includes(nextIndex)) {
          nextIndex++;
        } else {
          // Found a task with travel time, stop here
          break;
        }
      }
    }

    // Sort indices to process them in order
    const sortedIndices = Array.from(tasksToRecalculate).sort((a, b) => a - b);
    // Process each task that needs recalculation
    for (const taskIndex of sortedIndices) {
      const task = tasksForDate[taskIndex];
      let prevCoords;

      if (taskIndex === 0) {
        prevCoords = agent.coordinates;
      } else {
        prevCoords = tasksForDate[taskIndex - 1].coordinates;
      }
      const taskToUpdate = agent.details.find(detail =>
        detail[ZDATERANGE] === task[ZDATERANGE] &&
        Object.keys(detail).every(key => detail[key] === task[key])
      );
      if (taskToUpdate) {
        // Calculate travel time to POI if exists
        if (task.pointsOfInterest?.length > 0) {
          const poiTravelTime = await this.calculateTravelTime(
            prevCoords,
            task.pointsOfInterest[0].coordinates
          );
          const taskTravelTime = await this.calculateTravelTime(
            task.pointsOfInterest[0].coordinates,
            task.coordinates
          );
          taskToUpdate.poiTravelTime = (poiTravelTime + this.parseTime(timeATPOI));
          const travelTime = (taskTravelTime + (poiTravelTime + this.parseTime(timeATPOI)));
          taskToUpdate.travelTimeTo = travelTime;
        } else {
          // Direct travel to task
          const travelTime = await this.calculateTravelTime(prevCoords, task.coordinates);
          taskToUpdate.travelTimeTo = travelTime;
          taskToUpdate.poiTravelTime = 0;  // Reset POI travel time if no POI
        }
        // update back to task payload field
        const tskTrvlVal = this.minutesToTimeString(Math.round(taskToUpdate.travelTimeTo * 60));
        taskToUpdate[tskTravelTimeField] = tskTrvlVal;
      }
    }
    // Calculate total time including POI travel times
    let totalTime = tasksForDate.reduce((sum, detail) => {
      return sum + (detail.travelTimeTo || 0) + this.parseTime(detail[durationField]);
    }, 0);
    // Calculate final travel time considering POIs
    let finalTravelTime = 0;
    const lastTask = tasksForDate[tasksForDate.length - 1];

    if (agent.pointsOfInterest?.length > 0) {
      const matchingPOI = agent.pointsOfInterest.find(poi =>
        poi.POIASSIGNDATE?.startsWith(mapVisibilityDate)
      );

      if (matchingPOI) {
        const poiTravelTime = await this.calculateTravelTime(
          lastTask.coordinates,
          matchingPOI.coordinates
        );
        const homeTravelTime = await this.calculateTravelTime(
          matchingPOI.coordinates,
          coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates
        );
        finalTravelTime = (poiTravelTime + homeTravelTime + this.parseTime(timeATPOI));
      } else {
        finalTravelTime = await this.calculateTravelTime(lastTask.coordinates,
          coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates);
      }
    } else {
      finalTravelTime = await this.calculateTravelTime(lastTask.coordinates,
        coordinatesInfo.type === "END" ? coordinatesInfo.coordinates : agent.coordinates);
    }


    // Final TravelTime update Logic
    const matchIndex = updatedTravelData.findIndex(data =>
      data.TRAVELER === agent[selectedtraveler] &&
      data.MAPNAME === selectedSourceMapname &&
      data.TRAVELDATE.startsWith(mapVisibilityDate) &&
      data.TRAVLRTYPE === "TeamLeader"
    );

    if (matchIndex !== -1) {
      updatedTravelData[matchIndex] = {
        ...updatedTravelData[matchIndex],
        TRAVELTIME: this.formatTime(finalTravelTime)
      };
    } else {
      updatedTravelData.push({
        MAPNAME: selectedSourceMapname,
        TRAVELDATE: mapVisibilityDate,
        TRAVELER: agent[selectedtraveler],
        TRAVELTIME: this.formatTime(finalTravelTime),
        TRANSID: 0,
        USERPOI: "",
        TRAVLRTYPE: "TeamLeader",
        TEAMLEADER: agent[selectedtraveler]
      });
    }
    //  Return updated agent with filtered records for the current traveler
    return {
      ...agent,
      totalTime: totalTime + finalTravelTime,
      finalTravelTime: updatedTravelData.filter(data =>
        data.TRAVELER === agent[selectedtraveler] &&
        data.TRAVLRTYPE === "TeamLeader"
      )
    };
  }

  static getScheduleBreakdown(agent, maxHours, durationField) {
    if (!agent?.details?.length) return null;

    const breakdown = {
      totalTime: agent.totalTime || 0,
      isOvertime: (agent.totalTime || 0) > maxHours,
      segments: agent.details.map(task => ({
        travelTime: this.minutesToTimeString(Math.round(task.travelTimeTo * 60)) || 0,
        taskDuration: task[durationField] ? this.parseTime(task[durationField]) : 0,
        poiTravelTime: task.poiTravelTime || 0
      })),
      finalTravelTime: agent.finalTravelTime || 0
    };

    return breakdown;
  }

  // Helper function to parse time string in "HH:mm" format to minutes
  static parseTimeToMinutes(timeStr) {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  static minutesToTimeString(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const result = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    return result === '00:00' ? '00:01' : result;
  }

  // New function to recalculate task times based on order
  static recalculateTaskTimes(
    tasks,
    startTimeField,
    endTimeField,
    durationFieldsY,
    dayStart,
    tskTravelTimeField,
    fixschedulefield,
    mapVisibilityDate,
    ZDATERANGE
  ) {
    if (
      !tasks || tasks.length === 0 ||
      !startTimeField || !endTimeField ||
      !durationFieldsY
    ) return tasks;

    const dayStartValue = (!dayStart || dayStart === "00:00") ? "09:00" : dayStart;
    let currentStartTime = this.parseTimeToMinutes(dayStartValue);

    return tasks.map((task, index) => {
      // Skip tasks without a matching date
      if (!task || typeof task[ZDATERANGE] !== "string" || !task[ZDATERANGE].startsWith(mapVisibilityDate)) {
        return task;
      }
      const taskCopy = { ...task };
      // Parse travel time
      const travelTimeHours = typeof taskCopy.travelTimeTo === 'number'
        ? taskCopy.travelTimeTo
        : (typeof taskCopy.travelTimeTo === 'string'
          ? this.parseTimeToMinutes(taskCopy.travelTimeTo) / 60
          : 0);

      // Handle fixed schedule
      if (taskCopy[fixschedulefield] === "Y") {
        const fixedTaskStartTime = this.parseTimeToMinutes(taskCopy[startTimeField]);
        const fixedTaskEndTime = this.parseTimeToMinutes(taskCopy[endTimeField]);

        const nextTask = tasks[index + 1];
        if (nextTask && nextTask[fixschedulefield] === "Y") {
          const nextTravelHours = typeof nextTask.travelTimeTo === 'number'
            ? nextTask.travelTimeTo
            : (typeof nextTask.travelTimeTo === 'string'
              ? this.parseTimeToMinutes(nextTask.travelTimeTo) / 60
              : 0);

          const nextTaskAdjustedStartTime = this.parseTimeToMinutes(nextTask[startTimeField]) - (nextTravelHours * 60);
          if (nextTaskAdjustedStartTime <= fixedTaskEndTime) {
            throw new Error("Task schedule overlaps with next fixed task.");
          }
        }

        currentStartTime = fixedTaskEndTime;

        return {
          ...taskCopy,
          [startTimeField]: this.minutesToTimeString(fixedTaskStartTime),
          [endTimeField]: this.minutesToTimeString(fixedTaskEndTime),
          travelTimeTo: travelTimeHours,
          [tskTravelTimeField]: this.minutesToTimeString(Math.round(travelTimeHours * 60))
        };
      }

      // Non-fixed tasks
      if (travelTimeHours > 0) {
        currentStartTime += Math.round(travelTimeHours * 60);
      }

      const duration = this.parseTimeToMinutes(taskCopy[durationFieldsY]);
      const endTime = currentStartTime + duration;

      // Check for overlap with future fixed task
      const nextFixedTask = tasks.slice(index + 1).find((next) => {
        if (next[fixschedulefield] === "Y") {
          const nextTravelHours = typeof next.travelTimeTo === 'number'
            ? next.travelTimeTo
            : (typeof next.travelTimeTo === 'string'
              ? this.parseTimeToMinutes(next.travelTimeTo) / 60
              : 0);

          const nextStart = this.parseTimeToMinutes(next[startTimeField]) - (nextTravelHours * 60);
          return nextStart <= endTime;
        }
        return false;
      });

      if (nextFixedTask) {
        throw new Error("Task schedule overlaps with fixed task.");
      }
      const updatedTask = {
        ...taskCopy,
        [startTimeField]: this.minutesToTimeString(currentStartTime),
        [endTimeField]: this.minutesToTimeString(endTime),
        travelTimeTo: travelTimeHours,
        [tskTravelTimeField]: this.minutesToTimeString(Math.round(travelTimeHours * 60))
      };

      currentStartTime = endTime;
      return updatedTask;
    });
  }


  static recalculatecombinedDataTaskTimes(modifiedTasksWithNewTimes, combinedData, tableUniqueName,
    tskTravelTimeField, startTimeField, endTimeField) {
    let copyCombinedData = JSON.parse(JSON.stringify(combinedData));

    modifiedTasksWithNewTimes.forEach(updatedTask => {
      // Normalize travelTimeTo to a number
      const travelTimeHours = typeof updatedTask.travelTimeTo === 'number'
        ? updatedTask.travelTimeTo
        : (typeof updatedTask.travelTimeTo === 'string'
          ? this.parseTimeToMinutes(updatedTask.travelTimeTo) / 60
          : 0);

      copyCombinedData.forEach(traveler => {
        if (!traveler.details) return;

        traveler.details = traveler.details.map(task => {
          if (task[tableUniqueName] === updatedTask[tableUniqueName]) {
            return {
              ...task,
              [startTimeField]: updatedTask[startTimeField],
              [endTimeField]: updatedTask[endTimeField],
              recordchange: true,
              travelTimeTo: travelTimeHours,
              [tskTravelTimeField]: this.minutesToTimeString(Math.round(travelTimeHours * 60)),
              poiTravelTime: updatedTask.poiTravelTime || 0
            };
          }
          return task;
        });
      });
    });

    return copyCombinedData;
  }
  /* Check for the selected date where user has POI and there is no details,
  If any POI record exist, then delete that POI for that date */
  static reassignuserPOI(updatedCombinedPayload, ZDATERANGE, mapVisibilityDate, selectedtraveler) {
    updatedCombinedPayload.forEach(cmbdata => {
      // Check if any record in cmbdata.details exists where CURDATE matches mapVisibilityDate and with matching user
      const detailExists = cmbdata.details.some(detail => {
        // Convert CURDATE to a string in the "YYYY-MM-DD" format
        const curDateString = new Date(detail[ZDATERANGE]).toISOString().split('T')[0];
        // Check if CURDATE starts with mapVisibilityDate and the selectedTraveler condition is met
        return curDateString === mapVisibilityDate && detail[selectedtraveler] === cmbdata[selectedtraveler];
      });

      // Check if 'details' is empty and if there's a record in pointsOfInterest
      if (!detailExists) {
        if (cmbdata.pointsOfInterest) {
          if (cmbdata.pointsOfInterest.length > 0) {
            // Loop through pointsOfInterest and delete if conditions match
            cmbdata.pointsOfInterest = cmbdata.pointsOfInterest.filter(poi => {
              if (poi.POIASSIGNDATE === mapVisibilityDate) {
                // Delete the POI if found
                return false;
              }
              return true;
            });
            // Now loop through finalTravelTime and update based on USERPOI and TRAVELDATE condition
            cmbdata.finalTravelTime = cmbdata.finalTravelTime.map(travel => {
              if (travel.TRAVELDATE.startsWith(mapVisibilityDate) && travel.TRAVELER === cmbdata[selectedtraveler]) {
                // Modify the travel if it meets the condition
                return {
                  ...travel,
                  USERPOI: "", // or perform any other action
                };
              }
              return travel;
            });
          }
        }
      }
    });
  }

  static handleAddparticipants(UpdateData, destAgentIndex, participantsFields, selectedTask, tableUniqueName) {
    // Get the block to add the participants from inpUpdateData
    const participantsblock = UpdateData.find((item, index) => index === destAgentIndex);

    // Extract participants
    const participantsList = participantsblock.ZCLA_TEAMMEMBERSR_SUBFORM.map(member => member.USERLOGIN);

    // Update the participants with details block-deep copy
    const detailswithParticipants = JSON.parse(JSON.stringify(participantsblock.details));

    detailswithParticipants.forEach(detail => {
      //changes only for the selected task
      if (detail[tableUniqueName] === selectedTask) {
        // Loop through extracted USERLOGIN fields and update with snameList
        participantsFields.forEach((loginField, index) => {
          if (index < participantsList.length) {
            detail[loginField] = participantsList[index];
            detail.recordchange = true;
          } else {
            detail[loginField] = null;
            detail.recordchange = true;
          }
        });
      }
    });
    return detailswithParticipants;
  }

  // get the alternate travel coordiantes
  static getValidCoordinates(traveler, mapVisibilityDate) {
    // Default coordinates
    let coordinatesToUse = traveler.coordinates;
    if (
      traveler.alternateAddress &&
      Array.isArray(traveler.alternateAddress) &&
      traveler.alternateAddress.length > 0
    ) {
      // Find the one record matching the date
      const altRecord = traveler.alternateAddress.find(
        alt => alt.ALTERDATE && alt.ALTERDATE.startsWith(mapVisibilityDate)
      );

      if (altRecord && altRecord.ALTERCOORDINATE) {
        const isDayStart = altRecord.DAYSTART === 'Y';
        const isDayEnd = altRecord.DAYEND === 'Y';

        if (isDayStart && isDayEnd) {
          return {
            type: 'BOTH',
            coordinates: altRecord.ALTERCOORDINATE
          };
        } else if (isDayStart) {
          return {
            type: 'START',
            coordinates: altRecord.ALTERCOORDINATE
          };
        } else if (isDayEnd) {
          return {
            type: 'END',
            coordinates: altRecord.ALTERCOORDINATE
          };
        }
      }
    }
    return {
      type: 'DEFAULT',
      coordinates: coordinatesToUse
    };
  };

  static formatUsername(username) {
    if (!username) return '?';

    // Handle formats like "UserName" by adding spaces before capital letters
    const formatted = username.replace(/([A-Z])/g, ' $1').trim();

    // Handle formats like "User_Name" by replacing underscores with spaces
    const withSpaces = formatted.replace(/_/g, ' ');

    // Get initials
    return withSpaces
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Full address function to concatenate address fields into a single string
  static getFullAddress(item, addressFields) {
    const address = addressFields
      .map(field => item[field])
      .filter(Boolean)
      .join(', ');
    return address.trim();
  };

  static async handleSaveCoorinates(zAddress, zFormattedAddress, { lat, lng }) {
    const ZlatLong = { lat, lng };
    const Zlat = ZlatLong.lat;
    const Zlng = ZlatLong.lng;

    const payloadCoordinates = {
      "FULLADDRESS": zAddress.slice(0, 120),
      "FULLADDRESSCONT": zAddress.length > 120 ? zAddress.slice(120) : "",
      "FORMATADDRESS": zFormattedAddress.slice(0, 120),
      "FORMATADDRESSCONT": zFormattedAddress.length > 120 ? zFormattedAddress.slice(120) : "",
      "LATITUDE": Zlat,
      "LONGITUDE": Zlng,
    }
    const postResponse = await MainService.postCoordinates(payloadCoordinates);
  }

  static async getCoordinates(address) {
    //const apiKey =  process.env.REACT_APP_API_KEY; 
    // get the googleapiKey
    if (!this.googleapiKey) {
      await this.getCompanyData();
    }
    const apiKey = this.googleapiKey;
    if (!apiKey) {
      console.error("API key is missing");
      return null;
    }
    if (!address || address.trim() === '') {
      console.warn("Empty address provided, skipping geocoding");
      return null;
    }
    const formattedAddress = address.replace(/[\/(),'!"£^&%$*@%\s]/g, '').toUpperCase();
    try {
      // get the coordinates from priority based on address
      const addressResponse = await MainService.getCoordinates(formattedAddress);
      // Call google API and creates new  coordinates if not exist in priority 
      if (addressResponse.data.value.length === 0) {

        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;

        try {
          const response = await fetch(url);
          const data = await response.json();
          if (data.results && data.results.length > 0) {
            const { lat, lng } = data.results[0].geometry.location;
            // Save the new coordinates in Priority
            this.handleSaveCoorinates(address, formattedAddress, { lat, lng });
            return { lat, lng };
          }
        } catch (error) {
          console.error("Error geocoding address:", error);
        }
        return null;
      }
      else {
        // Assign the coordinates to varaible from Priority
        const lat = addressResponse.data.value[0].LATITUDE;
        const lng = addressResponse.data.value[0].LONGITUDE;
        return { lat, lng };
      }
    } catch (err) {
      console.error("Error fetching Coordinates:", err);
    }
  };

  static handlecheckisUserHoliday(holidayPayload, selectedtraveler,
    agentCode, mapVisibilityDate) {
    let isUserHoliday = false;
    if (holidayPayload.length >= 0) {
      const holidayUser = holidayPayload.filter(hlddetail => {
        const fromDate = hlddetail.FROMDATE?.split?.('T')[0];
        const toDate = hlddetail.TODATE?.split?.('T')[0];

        return (
          hlddetail[selectedtraveler] === agentCode &&
          fromDate <= mapVisibilityDate &&
          toDate >= mapVisibilityDate
        );
      });
      if (holidayUser.length !== 0) {
        isUserHoliday = true;
      }
    }
    return isUserHoliday;
  };
}

export default TimeCalculationService;